import { toast } from 'sonner';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import TopBar from '../components/TopBar';
import api from '../api';

function AdminRateConfig() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const response = await api.get('/api/rate-tiers');
      setTiers(response.data.tiers || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/rate-tiers', { tiers });
      toast.success('Barems actualitzats correctament');
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...tiers];
    newTiers[index][field] = field === 'interest_rate' ? parseFloat(value) : (value === '' || value === null ? null : parseFloat(value));
    setTiers(newTiers);
  };

  if (loading) {
    return (
      <div className="main-layout">
        <Sidebar />
        <div className="content-area" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-layout">
      <Sidebar />
      <div className="content-area">
        <TopBar 
          title={t('rateTiers.title')}
          actions={
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary"
              style={{ padding: '0.75rem 1.5rem' }}
            >
              {saving ? '⏳ Guardant...' : '💾 Guardar Canvis'}
            </button>
          }
        />

        <main style={{ padding: '2.5rem', maxWidth: '900px' }} className="fade-in">
          <div className="dark-card" style={{ marginBottom: '2rem', padding: '1.5rem', background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)' }}>
            <p style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.9)', lineHeight: '1.6' }}>
              <strong>{t('rateTiers.autoRates')}</strong><br/>
              {t('rateTiers.description')}
            </p>
          </div>

          {/* Tiers Editor */}
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            {tiers.map((tier, index) => (
              <div key={index} className="dark-card" style={{ padding: '2rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '700', color: 'white', marginBottom: '1.5rem' }}>
                  Barem {index + 1}
                </h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                      Import Mínim (€)
                    </label>
                    <input
                      type="number"
                      step="1000"
                      value={tier.min_capital}
                      onChange={(e) => updateTier(index, 'min_capital', e.target.value)}
                      style={{ 
                        width: '100%',
                        fontSize: '1.25rem',
                        fontWeight: '700',
                        padding: '0.75rem',
                        color: '#60a5fa'
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                      Import Màxim (€)
                    </label>
                    <input
                      type="number"
                      step="1000"
                      value={tier.max_capital || ''}
                      onChange={(e) => updateTier(index, 'max_capital', e.target.value)}
                      placeholder="Infinit (∞)"
                      style={{ 
                        width: '100%',
                        fontSize: '1.25rem',
                        fontWeight: '700',
                        padding: '0.75rem',
                        color: '#a855f7'
                      }}
                    />
                    <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.5rem' }}>
                      Deixar buit per infinit
                    </p>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem' }}>
                      {t('rateTiers.interestRate')}
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      value={tier.interest_rate}
                      onChange={(e) => updateTier(index, 'interest_rate', e.target.value)}
                      style={{ 
                        width: '100%',
                        fontSize: '1.25rem',
                        fontWeight: '700',
                        padding: '0.75rem',
                        color: '#34d399'
                      }}
                    />
                  </div>
                </div>

                {/* Visual Range Display */}
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '0.75rem', 
                  background: 'rgba(255,255,255,0.03)', 
                  borderRadius: '8px',
                  borderLeft: '3px solid #34d399'
                }}>
                  <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.8)' }}>
                    📊 Rang: {tier.min_capital.toLocaleString('es-ES')}€ - {tier.max_capital ? tier.max_capital.toLocaleString('es-ES') + '€' : '∞'} → <strong style={{ color: '#34d399' }}>{tier.interest_rate}%</strong>
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Example Calculations */}
          <div className="dark-card" style={{ marginTop: '2rem', padding: '2rem', background: 'rgba(168,85,247,0.1)', border: '1px solid rgba(168,85,247,0.3)' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '700', color: 'white', marginBottom: '1rem' }}>
              💡 Exemples de Càlcul
            </h3>
            <div style={{ display: 'grid', gap: '0.75rem', fontSize: '0.95rem', color: 'rgba(255,255,255,0.8)' }}>
              <div>• Préstec de <strong>45.000€</strong> → {tiers[0]?.interest_rate}% anual</div>
              <div>• Préstec de <strong>75.000€</strong> → {tiers[1]?.interest_rate}% anual</div>
              <div>• Préstec de <strong>120.000€</strong> → {tiers[2]?.interest_rate}% anual</div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

export default AdminRateConfig;
